import { Direction } from '@mui/material';
import { injectable } from 'inversify';
import { makeAutoObservable, reaction, toJS } from 'mobx';

import { STORAGE_KEY, USER_PROFILE_KEY } from '@libs/constants';
import { ThemeMode, UserInfo } from '@libs/types';

import { authTokenKey, refreshTokenKey } from './AuthStore';

type LanguageKey = 'en' | 'ar';
@injectable()
export class LocalStorageStore {
  private readonly storageKey = STORAGE_KEY;

  public data: Record<string, unknown> = {};

  constructor() {
    makeAutoObservable(this);

    if (typeof window !== 'undefined') {
      this.hydrateFromLocalStorage();

      reaction(
        () => toJS(this.data),
        (snapshot) => {
          try {
            localStorage.setItem(this.storageKey, JSON.stringify(snapshot));
          } catch (error) {
            console.warn('LocalStorageStore: Failed to persist to localStorage', error);
          }
        },
      );
    }
  }

  private hydrateFromLocalStorage() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as unknown;
      Object.assign(this.data, parsed ?? {});
    } catch (error) {
      console.warn('LocalStorageStore: Failed to hydrate from localStorage', error);
      // Reset to empty while preserving observability
      Object.keys(this.data).forEach((key) => delete this.data[key]);
    }
  }

  get theme(): ThemeMode {
    return (this.data['theme'] as ThemeMode) ?? 'light';
  }

  get direction(): Direction {
    return (this.data['direction'] as Direction) ?? 'ltr';
  }

  get lang(): LanguageKey {
    return (this.data['lang'] as LanguageKey) ?? 'en';
  }

  get authToken(): string | null {
    return this.data[authTokenKey] as string | null;
  }

  get refreshToken(): string | null {
    return this.data[refreshTokenKey] as string | null;
  }

  get userProfile(): UserInfo | null {
    return this.data[USER_PROFILE_KEY] as UserInfo | null;
  }

  set<T>(key: string, value: T): void {
    this.data[key] = value;
  }

  remove(key: string): void {
    delete this.data[key];
  }

  clear(): void {
    Object.keys(this.data).forEach((key) => delete this.data[key]);
  }
}
